import React, { useState } from 'react';
import { IosPushPreview } from 'components/IosPushPreview/IosPushPreview';
import { AndroidPushPreview } from 'components/AndroidPushPreview/AndroidPushPreview';
import { TextComponent } from 'baseComponents/TextComponent';
import { InputComponent } from 'baseComponents/InputComponent';
import { ButtonComponent } from 'baseComponents/ButtonComponent';
import { TabContentWrapper } from 'screens/CreateCampaignScreen/components/TabContentWrapper';
import './composeTabPush.scss';
import { notification, Radio } from 'antd';
import { useGetTestUsers } from 'hooks/useGetTestUsers';
import { parseMessagesTemplates, parseTestUsers } from 'helpers/dropdown-data';
import { useGetMessagesTemplates } from 'hooks/useGetMessagesTemplates';
import { FullScreenLoaderComponent } from 'baseComponents/FullScreenLoaderComponent';
import {
  useCreateCampaignStore,
  useCreateCampaignStoreActions,
} from 'store/CreateCampaignStore';
import { DropdownSelectComponent } from 'baseComponents/DropdownSelectComponent';
import { MultiSelectDropdownComponent } from 'baseComponents/MultiSelectDropdownComponent';
import { useSendTestMessage } from 'hooks/useSendTestMessage';
import { campaignMessagePayload } from 'helpers/create-campaign';

export const checkIfTemplateIsBlank = (
  tags: string[] | undefined,
  name: string | undefined
) => {
  return tags?.includes('UI_Tool_Blank') || name?.includes('Blank') || false;
};

export const ComposeTabPush: React.FC = () => {
  const [templateType, setTemplateType] = useState<string>('Deals');
  const title = useCreateCampaignStore((state) => state.title);
  const message = useCreateCampaignStore((state) => state.message);
  const messageTemplate = useCreateCampaignStore(
    (state) => state.messageTemplate
  );
  const dropdownLabel = useCreateCampaignStore(
    (state) => state.messageTemplate?.name ?? 'Select template'
  );
  const { setMessage, setCampaignTitle } = useCreateCampaignStoreActions();
  const { setMessageTemplate: setCampaignMessageTemplate } =
    useCreateCampaignStoreActions();

  const [testUsers, setTestUsers] = useState<string[]>([]);

  const isBlankTemplate = checkIfTemplateIsBlank(
    messageTemplate?.tags,
    messageTemplate?.name
  );

  // Check if inputs should be disabled based on template selection
  const isInputDisabled = Boolean(messageTemplate && !isBlankTemplate);

  const previewProps = {
    title: title || 'Your Notification Title',
    body: message || 'This is the body of your push notification.',
    appName: 'MyApp',
    time: 'now',
    iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg',
  };

  const {
    data: testUsersData,
    isLoading: isLoadingTestUsers,
    isRefetching: isRefetchingTestUsers,
    isError: isTestUsersError,
  } = useGetTestUsers('push');

  const {
    error: messagesTemplatesError,
    data: messagesTemplatesData,
    isLoading: isLoadingTemplates,
  } = useGetMessagesTemplates('push');

  const parsedMessagesTemplates = messagesTemplatesData
    ? parseMessagesTemplates(
        messagesTemplatesData.filter((item) =>
          item.tags.includes(`UI_Tool_${templateType}`)
        ),
        (item) => {
          setCampaignMessageTemplate(item);
          setCampaignTitle(
            checkIfTemplateIsBlank(item.tags, item.name)
              ? ''
              : (item.title ?? '')
          );
          setMessage(
            checkIfTemplateIsBlank(item.tags, item.name)
              ? ''
              : (item.message ?? '')
          );
        }
      )
    : [];

  const [api, contextHolder] = notification.useNotification();

  const sendTestMessage = useSendTestMessage({
    onSuccess: () => {
      return api.success({
        message: 'Test push has been sent!',
        description: 'Test push has been sent to test users.',
        duration: 3,
      });
    },
    onError: (data) => {
      return api.error({
        message: data?.message || 'Something went wrong',
        description:
          'Something went wrong sending test push. Please try again later.',
        duration: 3,
      });
    },
  });

  const handleSendToTestUsers = async () => {
    const templateId = messageTemplate?.campaign_id || null;
    const messagePayload = campaignMessagePayload(message, templateId, title);
    sendTestMessage.mutate({
      message: messagePayload,
      test_user_ids: testUsers.map((str) => parseInt(str)),
      channel: 'push',
    });
  };

  const parsedTestUsers = () => {
    return parseTestUsers(testUsersData || []);
  };

  const handleSelectTestUsers = (item: any) => {
    setTestUsers(item);
  };

  return (
    <TabContentWrapper tabTitle='Compose'>
      {contextHolder}
      <div className='compose-push'>
        <div className='compose-push__left-column'>
          <div className='compose-push__previews'>
            <IosPushPreview {...previewProps} />
          </div>
          <div className='compose-push__previews'>
            <AndroidPushPreview {...previewProps} />
          </div>
        </div>
        <div className='compose-push__right-column'>
          <div className='compose-push__input-box'>
            <TextComponent text='Template Type' weight='extra-light' />
            <Radio.Group
              options={['Deals', 'Menu', 'Loyalty', 'Other']}
              defaultValue='Deals'
              optionType='button'
              buttonStyle='solid'
              className='compose-push__radio-group'
              value={templateType}
              onChange={(e) => setTemplateType(e.target.value)}
            />
          </div>
          {!messagesTemplatesError && (
            <div className='compose-push__input-box'>
              <TextComponent text='Template' weight='extra-light' />
              <DropdownSelectComponent
                selectLabelValue={dropdownLabel}
                items={parsedMessagesTemplates || []}
                loading={isLoadingTemplates}
              />
            </div>
          )}
          {messageTemplate && (
            <>
              <div className='compose-push__input-box'>
                <TextComponent text='Title' weight='extra-light' />
                <InputComponent
                  value={title}
                  onChange={(e) => setCampaignTitle(e.target.value)}
                  placeholder='Notification title'
                  disabled={isInputDisabled}
                />
              </div>
              <div className='compose-push__input-box'>
                <TextComponent text='Message' weight='extra-light' />

                <InputComponent
                  textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={isInputDisabled}
                />
                <TextComponent
                  text={`Character count: ${message.length}`}
                  weight='extra-light'
                />
              </div>
            </>
          )}
          {!isTestUsersError && (
            <>
              {!isLoadingTestUsers &&
              !isRefetchingTestUsers &&
              parsedTestUsers().length === 0 ? (
                <div className='compose-push__input-box'>
                  <TextComponent text='No test users available' />
                </div>
              ) : (
                <>
                  <div className='compose-push__input-box'>
                    <TextComponent text='Test User' />
                    <MultiSelectDropdownComponent
                      options={parsedTestUsers()}
                      placeholder='Select test user'
                      onChange={handleSelectTestUsers}
                      value={testUsers}
                    />
                  </div>
                  <ButtonComponent
                    label='Send to Test Users'
                    onClick={handleSendToTestUsers}
                    size='large'
                    appereance='primary'
                    fullWidth
                    uppercase
                    className='compose-push__send-to-test-users'
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
      <FullScreenLoaderComponent
        spinning={isLoadingTestUsers || isRefetchingTestUsers}
      />
    </TabContentWrapper>
  );
};
